import React, { useState, useCallback } from "react";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import { DateRangePreset } from "../../../types/llm-usage.types";

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onDateChange: (startDate: string, endDate: string) => void;
  maxDays?: number;
}

const PRESETS: DateRangePreset[] = [
  { id: "today", label: "Dziś", days: 0 },
  { id: "yesterday", label: "Wczoraj", days: -1 },
  { id: "7-days", label: "Ostatnie 7 dni", days: 7 },
  { id: "30-days", label: "Ostatnie 30 dni", days: 30 },
  { id: "90-days", label: "Ostatnie 90 dni", days: 90 },
];

const formatDateForInput = (date: Date): string => {
  return format(date, "yyyy-MM-dd");
};

/**
 * Format date as YYYY-MM-DD string (no timezone conversion).
 * This ensures the date sent to backend matches user's local date.
 */
const formatDateOnly = (date: Date): string => {
  return format(date, "yyyy-MM-dd");
};

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onDateChange,
  maxDays = 365,
}) => {
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState(
    format(new Date(startDate), "yyyy-MM-dd")
  );
  const [customEnd, setCustomEnd] = useState(
    format(new Date(endDate), "yyyy-MM-dd")
  );
  const [error, setError] = useState<string | null>(null);

  // Determine which preset is active
  const getActivePreset = useCallback((): string | null => {
    const startStr = startDate.split("T")[0]; // Handle both YYYY-MM-DD and ISO
    const endStr = endDate.split("T")[0];
    const today = formatDateOnly(new Date());
    const yesterday = formatDateOnly(subDays(new Date(), 1));

    // Check for "yesterday" preset
    if (startStr === yesterday && endStr === yesterday) {
      return "yesterday";
    }

    // Check if end date is today for other presets
    if (endStr !== today) return null;

    const start = new Date(startStr);
    const end = new Date(endStr);
    const daysDiff = Math.round(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    );

    for (const preset of PRESETS) {
      if (preset.days === 0 && daysDiff === 0) return preset.id;
      if (preset.days > 0 && preset.days === daysDiff) return preset.id;
    }

    return null;
  }, [startDate, endDate]);

  const handlePresetClick = useCallback(
    (preset: DateRangePreset) => {
      const now = new Date();
      let start: Date;
      let end: Date;

      if (preset.days === -1) {
        // Yesterday: both start and end are yesterday
        const yesterday = subDays(now, 1);
        start = startOfDay(yesterday);
        end = endOfDay(yesterday);
      } else if (preset.days === 0) {
        // Today: both start and end are today
        start = startOfDay(now);
        end = endOfDay(now);
      } else {
        // Last N days: from N days ago to today
        start = startOfDay(subDays(now, preset.days));
        end = endOfDay(now);
      }

      setShowCustom(false);
      setError(null);
      // Send YYYY-MM-DD format to avoid timezone issues
      onDateChange(formatDateOnly(start), formatDateOnly(end));
    },
    [onDateChange]
  );

  const handleCustomApply = useCallback(() => {
    const start = new Date(customStart);
    const end = new Date(customEnd);

    // Validate
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      setError("Wybierz prawidłowe daty");
      return;
    }

    if (end < start) {
      setError("Data końcowa musi być po dacie początkowej");
      return;
    }

    const daysDiff = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysDiff > maxDays) {
      setError(`Maksymalny zakres to ${maxDays} dni`);
      return;
    }

    setError(null);
    setShowCustom(false);
    // Send YYYY-MM-DD format to avoid timezone issues
    onDateChange(formatDateOnly(start), formatDateOnly(end));
  }, [customStart, customEnd, maxDays, onDateChange]);

  const activePreset = getActivePreset();

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="date-range-picker">
      {PRESETS.map((preset) => (
        <button
          key={preset.id}
          type="button"
          onClick={() => handlePresetClick(preset)}
          data-testid={`preset-${preset.id}`}
          aria-pressed={activePreset === preset.id}
          className={`px-3 py-1.5 text-sm rounded-md border transition-colors focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:outline-none ${
            activePreset === preset.id
              ? "bg-primary-600 text-white border-primary-600"
              : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50"
          }`}
        >
          {preset.label}
        </button>
      ))}

      <button
        type="button"
        onClick={() => setShowCustom(!showCustom)}
        data-testid="preset-custom"
        aria-pressed={showCustom || activePreset === null}
        className={`px-3 py-1.5 text-sm rounded-md border transition-colors focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:outline-none ${
          showCustom || (activePreset === null && !showCustom)
            ? "bg-primary-600 text-white border-primary-600"
            : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50"
        }`}
      >
        Własny zakres
      </button>

      {showCustom && (
        <div className="flex flex-wrap items-center gap-2 mt-2 w-full sm:w-auto sm:mt-0">
          <div>
            <label htmlFor="date-start" className="sr-only">Data początkowa</label>
            <input
              id="date-start"
              type="date"
              value={customStart}
              onChange={(e) => {
                setCustomStart(e.target.value);
                setError(null);
              }}
              data-testid="date-start"
              className="px-3 py-1.5 text-sm border border-neutral-300 rounded-md focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:outline-none"
              max={formatDateForInput(new Date())}
            />
          </div>
          <span className="text-neutral-500">do</span>
          <div>
            <label htmlFor="date-end" className="sr-only">Data końcowa</label>
            <input
              id="date-end"
              type="date"
              value={customEnd}
              onChange={(e) => {
                setCustomEnd(e.target.value);
                setError(null);
              }}
              data-testid="date-end"
              className="px-3 py-1.5 text-sm border border-neutral-300 rounded-md focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:outline-none"
              max={formatDateForInput(new Date())}
            />
          </div>
          <button
            type="button"
            onClick={handleCustomApply}
            data-testid="date-apply"
            className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:outline-none"
          >
            Zastosuj
          </button>
        </div>
      )}

      {error && (
        <div className="w-full text-sm text-red-600 mt-1" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
